import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/dashboard/shared";
import { AudiencePicker, AudienceOption, TEST_AUDIENCE } from "@/components/dashboard/audience-picker";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type Audience = {
  key: string;
  label: string;
  category: AudienceOption["category"];
  needs_city: boolean;
  users: number | null;
  devices: number | null;
};

type Campaign = {
  id: string;
  title: string;
  body: string;
  audience: string;
  test_email: string | null;
  status: "sending" | "sent" | "failed" | "no_recipients";
  recipient_users: number;
  devices: number;
  sent_count: number;
  failed_count: number;
  error: string | null;
  created_at: string;
  sent_at: string | null;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS_LABELS: Record<Campaign["status"], string> = {
  sending: "Sending",
  sent: "Sent",
  failed: "Failed",
  no_recipients: "No recipients",
};

export function NotificationsPage() {
  const apiBase = getAPIBaseURL();
  const queryClient = useQueryClient();
  const [audience, setAudience] = React.useState("active_users_30d");
  const [city, setCity] = React.useState("");
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [testEmail, setTestEmail] = React.useState("");

  const citiesQuery = useQuery({
    queryKey: ["audience-cities"],
    queryFn: async () => {
      const response = await fetch(`${apiBase}/api/v1/admin/audiences/cities`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load cities");
      const json = await response.json();
      return (json.cities || []) as string[];
    },
  });

  const audiencesQuery = useQuery({
    queryKey: ["notification-audiences", city],
    queryFn: async () => {
      const query = city ? `?city=${encodeURIComponent(city)}` : "";
      const response = await fetch(`${apiBase}/api/v1/admin/notifications/audiences${query}`, {
        headers: getAdminHeaders(),
      });
      if (!response.ok) throw new Error("Failed to load audiences");
      const json = await response.json();
      return (json.audiences || []) as Audience[];
    },
  });

  const campaignsQuery = useQuery({
    queryKey: ["notification-campaigns"],
    queryFn: async () => {
      const response = await fetch(`${apiBase}/api/v1/admin/notifications/campaigns`, {
        headers: getAdminHeaders(),
      });
      if (!response.ok) throw new Error("Failed to load campaigns");
      const json = await response.json();
      return (json.campaigns || []) as Campaign[];
    },
  });

  const send = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${apiBase}/api/v1/admin/notifications/campaigns`, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          audience,
          city: selected?.needs_city ? city : undefined,
          test_email: audience === TEST_AUDIENCE ? testEmail.trim() : undefined,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.error || `Send failed (${response.status})`);
      return json;
    },
    onSuccess: (json) => {
      if (json.status === "no_recipients") {
        toast.error("No devices are registered in this audience.");
      } else {
        const campaign = json.campaign as Campaign;
        toast.success(`Sent to ${campaign.sent_count} device(s), ${campaign.failed_count} failed.`);
      }
      setTitle("");
      setBody("");
      queryClient.invalidateQueries({ queryKey: ["notification-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["notification-audiences"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
      queryClient.invalidateQueries({ queryKey: ["notification-campaigns"] });
    },
  });

  const audiences = audiencesQuery.data ?? [];
  const selected = audiences.find((item) => item.key === audience);
  const isTest = audience === TEST_AUDIENCE;
  const testEmailValid = EMAIL_PATTERN.test(testEmail.trim());
  const cityMissing = !isTest && !!selected?.needs_city && !city;
  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (!isTest || testEmailValid) &&
    !cityMissing &&
    !send.isPending;
  const pickerOptions: AudienceOption[] = audiences.map((item) => ({
    key: item.key,
    label: item.label,
    category: item.category,
    needs_city: item.needs_city,
    count: item.needs_city && !city ? null : item.users,
    detail: item.needs_city && !city ? undefined : `${item.devices ?? 0} device(s)`,
  }));

  const audienceLabel = isTest ? `test account ${testEmail.trim()}` : selected?.label ?? audience;
  const recipientSummary = isTest
    ? "Only the account with this email, on every device it has registered"
    : cityMissing
      ? "Choose a city to see its recipients"
      : selected
        ? `${selected.devices ?? 0} device(s) for ${selected.users ?? 0} user(s)`
        : "Loading recipients…";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Send push notifications to a chosen audience. The history below is the real send log."
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">New campaign</h3>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Audience</label>
              <AudiencePicker
                audiences={pickerOptions}
                value={audience}
                onChange={setAudience}
                city={city}
                onCityChange={setCity}
                cities={citiesQuery.data ?? []}
                testSelected={isTest}
                onTestSelect={() => setAudience(TEST_AUDIENCE)}
              />
              <p className="text-xs text-muted-foreground">{recipientSummary}</p>
            </div>

            {isTest && (
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="test-email">
                  Account email
                </label>
                <Input
                  id="test-email"
                  type="email"
                  placeholder="name@example.com"
                  value={testEmail}
                  onChange={(event) => setTestEmail(event.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="notification-title">
                Title
              </label>
              <Input
                id="notification-title"
                maxLength={80}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="New deals available"
              />
              <p className="text-right text-xs text-muted-foreground">{title.length}/80</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="notification-body">
                Message
              </label>
              <Textarea
                id="notification-body"
                maxLength={240}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder="Check out the offers from shops near you."
              />
              <p className="text-right text-xs text-muted-foreground">{body.length}/240</p>
            </div>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={!canSend} className="w-full">
                  {send.isPending ? "Sending…" : "Send notification"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Send this notification?</AlertDialogTitle>
                  <AlertDialogDescription>
                    <span className="block font-medium text-foreground">{title.trim()}</span>
                    <span className="block">{body.trim()}</span>
                    <span className="mt-3 block">
                      To: {audienceLabel} — {recipientSummary}
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => send.mutate()}>Send</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Audiences</h3>
          </CardHeader>
          <CardContent>
            {audiencesQuery.isError ? (
              <p className="text-sm text-destructive">Could not load audiences.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Audience</TableHead>
                    <TableHead className="text-right">Users</TableHead>
                    <TableHead className="text-right">Devices</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audiences.map((item) => (
                    <TableRow key={item.key}>
                      <TableCell>{item.label}</TableCell>
                      <TableCell className="text-right">{item.users}</TableCell>
                      <TableCell className="text-right">{item.devices}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Campaign history</h3>
        </CardHeader>
        <CardContent>
          {campaignsQuery.isError ? (
            <p className="text-sm text-destructive">Could not load campaigns.</p>
          ) : (campaignsQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No campaigns sent yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sent</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead className="text-right">Devices</TableHead>
                  <TableHead className="text-right">Delivered</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(campaignsQuery.data ?? []).map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell className="whitespace-nowrap">
                      {new Date(campaign.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{campaign.title}</div>
                      <div className="text-xs text-muted-foreground">{campaign.body}</div>
                    </TableCell>
                    <TableCell>
                      {campaign.audience === TEST_AUDIENCE
                        ? `Test: ${campaign.test_email ?? ""}`
                        : audiences.find((item) => item.key === campaign.audience)?.label ?? campaign.audience}
                    </TableCell>
                    <TableCell className="text-right">{campaign.devices}</TableCell>
                    <TableCell className="text-right">{campaign.sent_count}</TableCell>
                    <TableCell className="text-right">{campaign.failed_count}</TableCell>
                    <TableCell>
                      {STATUS_LABELS[campaign.status] ?? campaign.status}
                      {campaign.error && (
                        <div className="max-w-xs truncate text-xs text-destructive" title={campaign.error}>
                          {campaign.error}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
